import { useCallback, useEffect, useState } from 'react';
import { message } from 'antd';
import { Department as DepartmentType, UserDetail } from '@shared/types';
import { departmentsService } from '@/services/departments.service';
import { usersService } from '@/services/users.service';
import { handleApiError } from '@/utils/errorHandler';
import type { DepartmentDetail } from '@/types/department';

// Layer: Feature hook – xử lý toàn bộ logic nghiệp vụ quản lý phòng ban (fetch/map/filter/pagination/cache).

/**
 * Cache users tối thiểu để:
 * - Tính số nhân sự theo phòng ban
 * - Map leaderId -> { tên, avatar }
 *
 * Mục tiêu: giảm số lần gọi API `getUsers` lặp lại theo mỗi trang / thao tác filter.
 */
type CachedUserLite = Pick<UserDetail, 'id' | 'fullName' | 'avatar' | 'departmentId' | 'isDeleted' | 'isActive'>;

type UsersCachePayload = { ts: number; data: CachedUserLite[] };

const USERS_CACHE_KEY = 'dept_users_cache_v1';
const USERS_CACHE_TTL_MS = 5 * 60 * 1000; // 5 phút

let inMemoryUsersCache: UsersCachePayload | null = null;

const readUsersCache = (): UsersCachePayload | null => {
  if (inMemoryUsersCache && Date.now() - inMemoryUsersCache.ts < USERS_CACHE_TTL_MS) return inMemoryUsersCache;
  try {
    const raw = sessionStorage.getItem(USERS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as UsersCachePayload;
    if (!parsed?.ts || !Array.isArray(parsed.data)) return null;
    if (Date.now() - parsed.ts >= USERS_CACHE_TTL_MS) return null;
    inMemoryUsersCache = parsed;
    return parsed;
  } catch {
    return null;
  }
};

const writeUsersCache = (users: CachedUserLite[]) => {
  const payload: UsersCachePayload = { ts: Date.now(), data: users };
  inMemoryUsersCache = payload;
  try {
    sessionStorage.setItem(USERS_CACHE_KEY, JSON.stringify(payload));
  } catch {
    // ignore: sessionStorage có thể bị full hoặc bị chặn trong một số môi trường
  }
};

// Fetch users dạng "lite" với cache sessionStorage + in-memory
const fetchUsersLite = async (): Promise<CachedUserLite[]> => {
  const cached = readUsersCache();
  if (cached) return cached.data;

  // Lấy danh sách users 1 lần (có cache). Tăng limit để giảm số trang/phân trang (nếu backend cho phép).
  const usersResponse = await usersService.getUsers({ limit: 5000 });
  const lite = (usersResponse.data || []).map((u: UserDetail) => ({
    id: u.id,
    fullName: u.fullName,
    avatar: u.avatar,
    departmentId: u.departmentId,
    isDeleted: u.isDeleted,
    isActive: u.isActive,
  }));
  writeUsersCache(lite);
  return lite;
};

export interface UseDepartmentManagementResult {
  // filters
  searchInput: string;
  setSearchInput: (value: string) => void;
  searchQuery: string;
  statusFilter: boolean | null;
  setStatusFilter: (value: boolean | null) => void;

  // data
  departmentData: DepartmentDetail[];
  loading: boolean;
  total: number;
  availableHeads: Array<{ id: string; name: string; avatar?: string }>;
  error: string | null;

  // pagination
  currentPage: number;
  pageSize: number;
  handlePageChange: (page: number, size?: number) => void;
  handlePageSizeChange: (current: number, size: number) => void;

  // modals & selection
  isAddModalOpen: boolean;
  isEditModalOpen: boolean;
  selectedDepartmentItem: DepartmentDetail | null;
  handleAddDepartment: () => void;
  handleEditDepartment: (record: DepartmentDetail) => void;
  handleCloseAddModal: () => void;
  handleCloseEditModal: () => void;

  // actions
  deletingId: string | null;
  handleDeleteDepartment: (record: DepartmentDetail) => Promise<void>;
  handleSubmitAddDepartment: (data: any) => Promise<void>;
  handleSubmitEditDepartment: (data: any) => Promise<void>;
  handleClearFilter: () => void;
  handleSearch: () => void;
}

export const useDepartmentManagement = (): UseDepartmentManagementResult => {
  /** Danh sách phòng ban đã chuẩn hoá để hiển thị lên bảng */
  const [departmentData, setDepartmentData] = useState<DepartmentDetail[]>([]);

  /** Trạng thái loading tổng của trang (load phòng ban + dữ liệu phụ trợ) */
  const [loading, setLoading] = useState<boolean>(true);

  /** Phân trang */
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);

  /** Modal thêm/sửa */
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [selectedDepartmentItem, setSelectedDepartmentItem] = useState<DepartmentDetail | null>(null);

  /** Trạng thái đang xoá để khóa nút xoá theo từng dòng */
  const [deletingId, setDeletingId] = useState<string | null>(null);

  /** Danh sách user có thể chọn làm trưởng phòng */
  const [availableHeads, setAvailableHeads] = useState<Array<{ id: string; name: string; avatar?: string }>>([]);

  /**
   * Search theo 2 lớp:
   * - `searchInput`: người dùng đang gõ (không gọi API ngay)
   * - `searchQuery`: đã áp dụng khi bấm tìm kiếm / Enter
   */
  const [searchInput, setSearchInput] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');

  /** Filter trạng thái phòng ban */
  const [statusFilter, setStatusFilter] = useState<boolean | null>(null);

  /** Lỗi chung để page có thể hiển thị cho người dùng */
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch danh sách phòng ban từ API + chuẩn hoá dữ liệu hiển thị.
   * Tối ưu:
   * - Chỉ gọi `getUsers` 1 lần có cache (để tính số nhân sự + map trưởng phòng)
   * - Không gọi `getUserById` theo từng phòng ban (tránh N+1 requests)
   */
  const fetchDepartments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [response, allUsersLite] = await Promise.all([
        departmentsService.getDepartments({
          page: currentPage,
          limit: pageSize,
          search: searchQuery.trim() || undefined,
          isActive: statusFilter !== null ? statusFilter : undefined,
        }),
        fetchUsersLite(),
      ]);

      const leaderMap = new Map<number, CachedUserLite>();
      for (const u of allUsersLite) leaderMap.set(u.id, u);

      const personnelCountByDeptId = new Map<number, number>();
      for (const u of allUsersLite) {
        if (!u.departmentId) continue;
        if (u.isDeleted) continue;
        if (!u.isActive) continue;
        personnelCountByDeptId.set(u.departmentId, (personnelCountByDeptId.get(u.departmentId) || 0) + 1);
      }

      // Transform data từ API sang format của DepartmentDetail
      const transformedData: DepartmentDetail[] = response.data.map((dept: DepartmentType) => {
        // Trưởng phòng: ưu tiên lấy từ cache users theo leaderId
        const leader = dept.leaderId ? leaderMap.get(dept.leaderId) : undefined;
        const headInfo = dept.leaderId
          ? {
              id: dept.leaderId.toString(),
              name: leader?.fullName || dept.headEmployeeCode || 'Trưởng phòng',
              avatar: leader?.avatar,
            }
          : null;

        return {
          id: dept.id.toString(),
          code: dept.code,
          name: dept.name,
          description: '', // Backend hiện chưa có field description
          head: headInfo,
          personnelCount: personnelCountByDeptId.get(dept.id) || 0,
          status: dept.isActive ? 'active' : 'inactive',
        };
      });

      setDepartmentData(transformedData);
      setTotal(response.total);
    } catch (err: any) {
      // Dùng handleApiError để thống nhất cách hiển thị lỗi
      handleApiError(err, 'Không thể tải danh sách phòng ban');
      setError(err?.message || 'Không thể tải danh sách phòng ban');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchQuery, statusFilter]);

  /**
   * Fetch danh sách users để chọn làm trưởng phòng.
   * Tối ưu: dùng chung cache usersLite (tránh gọi API lần 2).
   */
  const fetchAvailableHeads = useCallback(async () => {
    try {
      const allUsersLite = await fetchUsersLite();
      const heads = allUsersLite.map((user) => ({ id: user.id.toString(), name: user.fullName, avatar: user.avatar }));
      setAvailableHeads(heads);
    } catch (err: any) {
      // Không block UI nếu danh sách trưởng phòng lỗi; modal sẽ không có options.
      handleApiError(err, 'Không thể tải danh sách trưởng phòng', false);
    }
  }, []);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  useEffect(() => {
    fetchAvailableHeads();
  }, [fetchAvailableHeads]);

  const handlePageChange = (page: number, size?: number) => {
    setCurrentPage(page);
    if (size) {
      setPageSize(size);
    }
  };

  const handlePageSizeChange = (current: number, size: number) => {
    setCurrentPage(current);
    setPageSize(size);
  };

  const handleAddDepartment = () => {
    setSelectedDepartmentItem(null);
    setIsAddModalOpen(true);
  };

  const handleEditDepartment = (record: DepartmentDetail) => {
    setSelectedDepartmentItem(record);
    setIsEditModalOpen(true);
  };

  const handleDeleteDepartment = async (record: DepartmentDetail): Promise<void> => {
    setDeletingId(record.id);
    try {
      await departmentsService.deleteDepartment(parseInt(record.id, 10));
      message.success('Xóa phòng ban thành công');
      await fetchDepartments();
    } catch (err: any) {
      handleApiError(err, 'Không thể xóa phòng ban');
      setError(err?.message || 'Không thể xóa phòng ban');
    } finally {
      setDeletingId(null);
    }
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
    setSelectedDepartmentItem(null);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedDepartmentItem(null);
  };

  const handleSubmitAddDepartment = async (data: any): Promise<void> => {
    try {
      await departmentsService.createDepartment({
        name: data.name,
        code: data.code,
        leaderId: data.headId ? parseInt(data.headId, 10) : undefined,
        headEmployeeCode: data.headId ? availableHeads.find((h) => h.id === data.headId)?.name : undefined,
        isActive: data.status === 'active',
      });

      message.success('Tạo phòng ban thành công');
      await fetchDepartments();
    } catch (err: any) {
      handleApiError(err, 'Không thể tạo phòng ban');
      setError(err?.message || 'Không thể tạo phòng ban');
      throw err;
    }
  };

  const handleSubmitEditDepartment = async (data: any): Promise<void> => {
    try {
      if (selectedDepartmentItem) {
        await departmentsService.updateDepartment(parseInt(selectedDepartmentItem.id, 10), {
          name: data.name,
          code: data.code,
          leaderId: data.headId ? parseInt(data.headId, 10) : undefined,
          headEmployeeCode: data.headId ? availableHeads.find((h) => h.id === data.headId)?.name : undefined,
          isActive: data.status === 'active',
        });

        message.success('Cập nhật phòng ban thành công');
        await fetchDepartments();
      }
    } catch (err: any) {
      handleApiError(err, 'Không thể cập nhật phòng ban');
      setError(err?.message || 'Không thể cập nhật phòng ban');
      throw err;
    }
  };

  const handleClearFilter = () => {
    setSearchInput('');
    setSearchQuery('');
    setStatusFilter(null);
    setCurrentPage(1);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setSearchQuery(searchInput.trim());
  };

  return {
    // filters
    searchInput,
    setSearchInput,
    searchQuery,
    statusFilter,
    setStatusFilter,

    // data
    departmentData,
    loading,
    total,
    availableHeads,
    error,

    // pagination
    currentPage,
    pageSize,
    handlePageChange,
    handlePageSizeChange,

    // modals & selection
    isAddModalOpen,
    isEditModalOpen,
    selectedDepartmentItem,
    handleAddDepartment,
    handleEditDepartment,
    handleCloseAddModal,
    handleCloseEditModal,

    // actions
    deletingId,
    handleDeleteDepartment,
    handleSubmitAddDepartment,
    handleSubmitEditDepartment,
    handleClearFilter,
    handleSearch,
  };
};


